/**
 * Shared utilities for {@link LevelRow}, {@link LevelCard} and {@link LevelsDataTable}.
 */
import type { Level } from '@/types/level.types';
import type { LevelFilters } from './level-configs';

/** Age range label (`min–max`, `min+`, or `≤ max`); `null` when both ages are unset. */
export function formatLevelAgeRange(level: Pick<Level, 'minAge' | 'maxAge'>): string | null {
  const { minAge, maxAge } = level;
  if (minAge != null && maxAge != null) return `${minAge}–${maxAge} ans`;
  if (minAge != null) return `${minAge}+ ans`;
  if (maxAge != null) return `≤ ${maxAge} ans`;
  return null;
}

/** Map UI filters + pagination to level list params. */
export function toLevelListParams(
  filters: LevelFilters,
  page: number,
  pageSize: number,
): {
  page: number;
  pageSize: number;
  cycles?: LevelFilters['cycles'];
  statuses?: LevelFilters['statuses'];
  search?: string;
  minAge?: number;
  maxAge?: number;
} {
  return {
    page,
    pageSize,
    cycles: filters.cycles.length > 0 ? filters.cycles : undefined,
    statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
    search: filters.searchTerm,
    minAge: filters.minAge,
    maxAge: filters.maxAge,
  };
}

/** True when any cycle / status / search / age filter is set. */
export function hasLevelActiveFilters(filters: LevelFilters): boolean {
  return (
    filters.cycles.length > 0 ||
    filters.statuses.length > 0 ||
    (!!filters.searchTerm && filters.searchTerm.length > 0) ||
    filters.minAge !== undefined ||
    filters.maxAge !== undefined
  );
}
